/** Prompt for a name + codename when promoting a category's inline schema into a
 *  reusable Tag_Category_Schema. */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useEffect, useState } from 'react';

const slugify = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

interface Props {
  open: boolean;
  defaultName: string;
  saving: boolean;
  onCancel: () => void;
  onConfirm: (v: { name: string; codename: string }) => void;
}

export function PromoteInlineSchemaDialog({ open, defaultName, saving, onCancel, onConfirm }: Props) {
  const [name, setName] = useState(defaultName);
  const [codename, setCodename] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setCodename('');
      setTouched(false);
    }
  }, [open, defaultName]);

  const effectiveCodename = touched ? codename : slugify(name);
  const canSubmit = name.trim().length > 0 && effectiveCodename.length > 0 && !saving;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Promote to reusable schema</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Schema name" size="small" fullWidth value={name} autoFocus
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 200 }} />
          <TextField label="Codename" size="small" fullWidth value={effectiveCodename}
            onChange={(e) => { setTouched(true); setCodename(e.target.value); }}
            helperText="Unique slug. The category will switch to referencing this new schema."
            inputProps={{ maxLength: 64 }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit}
          onClick={() => onConfirm({ name: name.trim(), codename: effectiveCodename })}>
          {saving ? 'Promoting…' : 'Promote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
